// src/components/snapsite/UserProfilePage.tsx
"use client";

import type { SnapSiteData } from '@/types';
import { useState, useEffect, useCallback } from 'react';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { Settings, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import CopyableInfoModal from './CopyableInfoModal';
import OfflineReadyBanner from './OfflineReadyBanner';
import { getLayout } from './layouts/registry';
import { getAccentColor, getPrimaryColor } from '@/lib/snapsite-colors';
import { getFontPair } from '@/lib/snapsite-fonts';
import type { LayoutProps } from './layouts/types';
import WhatsAppShareButton from './WhatsAppShareButton';
import { useRegisterSW } from '@/hooks/useRegisterSW';
import GalleryPublic from './GalleryPublic';
import { isAdultUrl } from '@/lib/adult-filter';

// Import theme CSS files for backward compatibility (existing users with old theme field)
import "@/themes/gris-oscuro.css";
import "@/themes/azul-petroleo.css";
import "@/themes/verde-corporate.css";
import "@/themes/rosa-suave.css";
import "@/themes/ambar.css";
import "@/themes/morado-lavanda.css";
import "@/themes/azul-suave.css";
import "@/themes/terminal-70s.css";

// Solo se usa al abrir el popup de QR — cargarlo perezosamente evita mandarlo en la
// carga inicial de cada perfil.
const QRCodeSVG = dynamic(() => import('qrcode.react').then(mod => mod.QRCodeSVG));

interface UserProfilePageProps {
  data: SnapSiteData;
}

// Layouts "look fijo" de borde a borde (cancelan el padding del wrapper con -mx-4/-my-8 en
// su propio archivo) — ver el comentario junto a su uso más abajo.
const FULL_BLEED_LAYOUTS = new Set(['halloween-light', 'halloween-dark', 'spectral-stripes', 'gothic-portrait']);

export default function UserProfilePage({ data }: UserProfilePageProps) {
  const { offlineReady, needsUpdate, updateSW } = useRegisterSW();

  const [showQrPopup, setShowQrPopup] = useState(false);
  const [showCopyableInfo, setShowCopyableInfo] = useState(false);
  const [pageUrl, setPageUrl] = useState('');
  const [copyButtonText, setCopyButtonText] = useState('Copiar Enlace');
  const [nativeShareAvailable, setNativeShareAvailable] = useState(false);

  // States for adult age verification modal
  const [showAdultModal, setShowAdultModal] = useState(false);
  const [pendingAdultUrl, setPendingAdultUrl] = useState<string | null>(null);
  const [hasConfirmedAge, setHasConfirmedAge] = useState(false);

  const appMainDomain =
    typeof window !== 'undefined'
      ? `${window.location.protocol}//${process.env.NEXT_PUBLIC_APP_MAIN_DOMAIN || 'snapsite.cc'}`
      : `https://${process.env.NEXT_PUBLIC_APP_MAIN_DOMAIN || 'snapsite.cc'}`;

  const appDashboardLink = `${appMainDomain}/dashboard`;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setPageUrl(window.location.href);
      if (typeof navigator.share === 'function') setNativeShareAvailable(true);
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setShowQrPopup(false); setShowCopyableInfo(false); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [data]);

  // Read hasConfirmedAge from sessionStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const confirmed = sessionStorage.getItem('snapsite_adult_confirmed') === 'true';
      setHasConfirmedAge(confirmed);
    }
  }, []);

  const handleGlobalClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (hasConfirmedAge) return;

    // Find the closest anchor tag 'a'
    const anchor = (e.target as HTMLElement).closest('a');
    if (anchor) {
      const href = anchor.getAttribute('href');
      if (href && isAdultUrl(href)) {
        e.preventDefault();
        setPendingAdultUrl(href);
        setShowAdultModal(true);
      }
    }
  }, [hasConfirmedAge]);

  const handleConfirmAge = () => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('snapsite_adult_confirmed', 'true');
    }
    setHasConfirmedAge(true);
    setShowAdultModal(false);
    if (pendingAdultUrl) {
      window.open(pendingAdultUrl, '_blank', 'noopener,noreferrer');
      setPendingAdultUrl(null);
    }
  };

  const handleCancelAge = () => {
    setShowAdultModal(false);
    setPendingAdultUrl(null);
  };

  const handleShowQr = () => { if (pageUrl) setShowQrPopup(true); };

  const handleDownloadQR = useCallback(() => {
    const qrSvgElement = document.getElementById('qrcodeDisplayInModal')?.querySelector('svg');
    if (!qrSvgElement) return;

    const qrSize = 500;
    const padding = 25;
    const cornerRadius = 12;
    const qrActualSize = qrSize - 2 * padding;
    const brandingHeight = 52;
    const canvas = document.createElement('canvas');
    canvas.width = qrSize;
    canvas.height = qrSize + brandingHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.moveTo(cornerRadius, 0);
    ctx.lineTo(qrSize - cornerRadius, 0);
    ctx.arcTo(qrSize, 0, qrSize, cornerRadius, cornerRadius);
    ctx.lineTo(qrSize, qrSize + brandingHeight - cornerRadius);
    ctx.arcTo(qrSize, qrSize + brandingHeight, qrSize - cornerRadius, qrSize + brandingHeight, cornerRadius);
    ctx.lineTo(cornerRadius, qrSize + brandingHeight);
    ctx.arcTo(0, qrSize + brandingHeight, 0, qrSize + brandingHeight - cornerRadius, cornerRadius);
    ctx.lineTo(0, cornerRadius);
    ctx.arcTo(0, 0, cornerRadius, 0, cornerRadius);
    ctx.closePath();
    ctx.fill();

    const serializer = new XMLSerializer();
    let svgString = '<?xml version="1.0" standalone="no"?>\r\n' + serializer.serializeToString(qrSvgElement);
    const img = new Image();
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      ctx.drawImage(img, padding, padding, qrActualSize, qrActualSize);
      URL.revokeObjectURL(url);
      ctx.strokeStyle = '#E5E7EB'; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(padding * 2, qrSize); ctx.lineTo(qrSize - padding * 2, qrSize); ctx.stroke();
      ctx.font = "bold 18px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
      ctx.fillStyle = '#111827'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(process.env.NEXT_PUBLIC_APP_MAIN_DOMAIN || 'snapsite.cc', qrSize / 2, qrSize + brandingHeight / 2);
      const pngUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = pngUrl; link.download = `${data?.username || 'qrcode_snapsite'}.png`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };
    img.src = url;
  }, [data?.username]);

  const handleGenerateVCF = useCallback(async () => {
    if (!data) return;
    let vcfContent = `BEGIN:VCARD\r\nVERSION:3.0\r\nFN:${data.name || ''}\r\n`;
    if (data.company) vcfContent += `ORG:${data.company}\r\n`;
    if (data.position) vcfContent += `TITLE:${data.position}\r\n`;
    if (data.phone) vcfContent += `TEL;TYPE=CELL,VOICE:${data.phone}\r\n`;
    if (data.ownerEmail && !data.hideContactEmail) vcfContent += `EMAIL:${data.ownerEmail}\r\n`;
    if (data.googleMapsLink) vcfContent += `URL;type=WORK;type=pref:${data.googleMapsLink}\r\n`;
    if (data.socialLinks?.website) vcfContent += `URL:${data.socialLinks.website}\r\n`;
    vcfContent += `URL:https://${process.env.NEXT_PUBLIC_APP_MAIN_DOMAIN || 'snapsite.cc'}/crear\r\n`;
    if (data.profileImageUrl && navigator.onLine) {
      try {
        const proxyUrl = `/api/image-proxy?imageUrl=${encodeURIComponent(data.profileImageUrl)}`;
        const response = await fetch(proxyUrl);
        if (!response.ok) throw new Error(`Proxy fetch failed`);
        const blob = await response.blob();
        if (blob.size < 1024 * 1024) {
          const reader = new FileReader();
          const base64StringWithPrefix = await new Promise<string>((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const commaIndex = base64StringWithPrefix.indexOf(',');
          if (commaIndex !== -1) {
            const actualBase64 = base64StringWithPrefix.substring(commaIndex + 1);
            let vcfImageType = blob.type.toUpperCase().split('/')[1];
            if (vcfImageType === 'JPG') vcfImageType = 'JPEG';
            if (actualBase64 && ['JPEG','PNG','GIF'].includes(vcfImageType)) {
              vcfContent += `PHOTO;ENCODING=b;TYPE=${vcfImageType}:${actualBase64}\r\n`;
            }
          }
        }
      } catch (imgError: any) {
        console.warn('VCF: Error processing profile image via proxy, skipping.', imgError.message);
      }
    }
    vcfContent += `NOTE:Perfil de ${data.name || 'contacto'} en SnapSite de .Site RTSI.\r\nEND:VCARD`;
    const blob = new Blob([vcfContent], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url; link.download = `${data.name?.replace(/\s+/g,'_').toLowerCase() || 'contacto'}.vcf`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [data]);

  const handleNativeShare = async () => {
    if (navigator.share && pageUrl && data) {
      try {
        await navigator.share({
          title: `${data.name} | SnapSite de .Site RTSI`,
          text: data.bio?.substring(0, 120) + '...' || `Conecta con ${data.name}.`,
          url: pageUrl,
        });
      } catch (err) { /* user cancelled */ }
    }
  };

  const handleCopyLink = () => {
    if (pageUrl) {
      navigator.clipboard.writeText(pageUrl).then(() => {
        setCopyButtonText('¡Enlace Copiado!');
        setTimeout(() => setCopyButtonText('Copiar Enlace'), 2000);
      });
    }
  };

  if (!data) return <div className="text-center p-10">Cargando perfil...</div>;

  // ── Color system ────────────────────────────────────────────────────────────
  // Migration: if accentColor not set, fall back to theme key for same colors
  const accentKey = data.accentColor || data.theme || 'default';
  const primaryKey = data.isPremium ? (data.primaryColor || data.theme || 'default') : 'default';

  const accent = getAccentColor(accentKey);
  const primary = getPrimaryColor(primaryKey);

  const fontPairKey = data.isPremium ? (data.fontPair || 'default') : 'default';
  const fontPair = getFontPair(fontPairKey);
  const googleFontsUrl = fontPair.key !== 'default' ? fontPair.googleFontsUrl : undefined;

  const colorStyle = {
    '--primary': accent.primary,
    '--primary-foreground': accent.primaryForeground,
    '--ring': accent.primary,
    ...(fontPairKey !== 'default' ? {
      '--font-heading': fontPair.heading.cssValue,
      '--font-body': fontPair.body.cssValue,
    } : {}),
    // Only apply background/card overrides for premium users with a non-default primary color
    ...(data.isPremium && primaryKey !== 'default' ? {
      '--background': primary.background,
      '--foreground': primary.foreground,
      '--card': primary.card,
      '--card-foreground': primary.cardForeground,
      '--muted': primary.muted,
      '--muted-foreground': primary.mutedForeground,
      '--border': primary.border,
      '--input': primary.input,
      '--secondary': primary.secondary,
      '--secondary-foreground': primary.secondaryForeground,
    } : {}),
  } as React.CSSProperties;

  // ── Layout selection ────────────────────────────────────────────────────────
  const layoutId = data.isPremium && data.layout ? data.layout : 'classic';
  const layoutConfig = getLayout(layoutId);
  const LayoutComponent = layoutConfig.component;

  const hasCopyableBlocks = !!(data.copyableBlocks && data.copyableBlocks.length > 0);

  const layoutProps: LayoutProps = {
    data,
    pageUrl,
    nativeShareAvailable,
    copyButtonText,
    onShowQr: handleShowQr,
    onDownloadQR: handleDownloadQR,
    onGenerateVCF: handleGenerateVCF,
    onNativeShare: handleNativeShare,
    onCopyLink: handleCopyLink,
    onShowCopyableInfo: () => setShowCopyableInfo(true),
    appMainDomain,
    galleryNode: data.isPremium && data.galleryActive && data.galleryModerationStatus !== 'pending_review' && data.galleryModerationStatus !== 'blocked' && data.galleryItems?.some(item => item.isPublic) ? <GalleryPublic title={data.galleryTitle || 'Galería'} items={data.galleryItems} /> : undefined,
    whatsAppShareNode: data.whatsappShareEnabled
      ? <WhatsAppShareButton profileUrl={pageUrl} profileName={data.name} />
      : undefined,
  };

  return (
    <>
      <OfflineReadyBanner offlineReady={offlineReady} needsUpdate={needsUpdate} onUpdate={updateSW} />
      {googleFontsUrl && (
        // <link> real en vez de @import dentro de un <style>: el preload scanner del
        // navegador lo descubre durante el parseo de HTML, en vez de tener que esperar a
        // que se parsee el CSSOM del <style> para recién ahí descubrir el @import.
        <link rel="stylesheet" href={googleFontsUrl} />
      )}
      <div
        onClick={handleGlobalClick}
        className={cn(
          "snapsite-profile-wrapper min-h-screen text-foreground py-8 px-4 flex flex-col items-center relative",
          // justify-center se ve bien cuando la tarjeta cabe en una pantalla, pero con
          // contenido más alto que el viewport dejaba un residuo de --background asomando
          // arriba y abajo (justify-content:center reparte el desborde por mitad a cada
          // lado). Los layouts "look fijo" de borde a borde (que además cancelan el padding
          // del wrapper con -mx-4/-my-8, ver sus propios archivos) casi siempre son más
          // altos que la pantalla, así que se alinean arriba en vez de centrarse.
          data.layout && FULL_BLEED_LAYOUTS.has(data.layout) ? "justify-start" : "justify-center",
          data.layout === 'minimal' ? "bg-background" : "bg-gradient-to-br from-background to-secondary/10",
          data.isPremium && primaryKey !== 'default' ? `theme-${primaryKey}` : ''
        )}
        style={colorStyle}
      >
        <LayoutComponent {...layoutProps} />

        {/* Copyright footer - only for non-premium */}
        {!data.isPremium && (
          <footer className="w-full max-w-2xl text-center pt-2">
            <p className="text-xs text-foreground">
              © {new Date().getFullYear()}{' '}
              <NextLink href={appMainDomain} className="text-primary/80 hover:text-primary hover:underline transition-colors">
                SnapSite
              </NextLink>{' '}
              de{' '}
              <a href="https://rtsi.site" target="_blank" rel="noopener noreferrer"
                className="text-primary/80 hover:text-primary hover:underline transition-colors">
                .Site RTSI
              </a>
              . Todos los derechos reservados.
            </p>
          </footer>
        )}

        {/* "Creado con SnapSite" pill - only for non-premium */}
        {!data.isPremium && (
          <div className="w-full max-w-2xl mx-auto mt-2 mb-1 text-center">
            <a
              href={appMainDomain}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-black/70 text-white/90 hover:bg-black hover:text-white border border-white/20 transition-all duration-200"
            >
              Creado con <span className="font-semibold">SnapSite</span> · Crea el tuyo gratis →
            </a>
          </div>
        )}

        {/* Botón editar — al final del contenido, alineado a la derecha. Los layouts Halloween
            (look fijo, tarjeta de borde a borde) meten su propia versión dentro de la tarjeta
            en vez de dejarla huérfana sobre el fondo de la página — ver HalloweenLayoutBase.tsx. */}
        {data.layout !== 'halloween-light' && data.layout !== 'halloween-dark' && data.layout !== 'spectral-stripes' && data.layout !== 'gothic-portrait' && (
          <div className="w-full max-w-2xl flex justify-end mt-2 mb-1">
            <a
              href={appDashboardLink}
              title="Editar mi SnapSite"
              className="group flex items-center gap-2 text-muted-foreground hover:text-muted-foreground transition-colors duration-200"
            >
              <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                Editar mi SnapSite
              </span>
              <Settings className="h-4 w-4 shrink-0" />
            </a>
          </div>
        )}

        {/* QR Popup */}
        {showQrPopup && pageUrl && (
          <div
            id="qrOverlay"
            className="fixed inset-0 bg-black/70 dark:bg-black/80 z-40 flex items-center justify-center p-4 animate-in fade-in-50"
            onClick={() => setShowQrPopup(false)}
            aria-modal="true"
            role="dialog"
          >
            <div
              className="bg-card p-6 rounded-lg shadow-2xl animate-in zoom-in-90 flex flex-col items-center gap-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div id="qrcodeDisplayInModal" className="bg-white p-4 rounded-md shadow-inner">
                <QRCodeSVG value={pageUrl} size={180} bgColor="#ffffff" fgColor="#333333" level="H" includeMargin={false} />
              </div>
              <Button variant="outline" size="sm" onClick={handleDownloadQR} className="w-full">
                <Download className="h-4 w-4 mr-2" /> Descargar QR
              </Button>
            </div>
          </div>
        )}

        {/* Copyable info modal */}
        {hasCopyableBlocks && (
          <CopyableInfoModal
            isOpen={showCopyableInfo}
            onClose={() => setShowCopyableInfo(false)}
            blocks={data.copyableBlocks || []}
          />
        )}

        {/* Adult age verification modal */}
        {showAdultModal && (
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200"
            onClick={handleCancelAge}
          >
            <div
              className="bg-card/90 backdrop-blur-xl border border-border/50 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-6 text-center animate-in zoom-in-95 duration-200"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex flex-col items-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-destructive/10 border border-destructive/20 flex items-center justify-center text-4xl animate-pulse">
                  🔞
                </div>
                <h2 className="text-xl font-bold text-foreground">Contenido para Adultos (18+)</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Este enlace conduce a un sitio web externo con contenido exclusivo para adultos. Debes tener la edad legal suficiente para poder ingresar.
                </p>
              </div>

              {pendingAdultUrl && (
                <div className="rounded-xl bg-muted/40 p-3.5 text-xs text-muted-foreground border border-border/30 break-all text-left font-mono">
                  <span className="font-semibold text-foreground block mb-1 font-sans">Enlace destino:</span>
                  {pendingAdultUrl}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleConfirmAge}
                  className="flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground font-semibold h-11"
                >
                  Tengo más de 18 años
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancelAge}
                  className="flex-1 border-border/60 hover:bg-muted text-foreground font-medium h-11"
                >
                  Volver
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
